import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';

const STATUS_OPTIONS = ['pending', 'approved', 'rejected'];

export default function AdminReviewsPage() {
  const { token } = useContext(AuthContext);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');

  const loadReviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/reviews', { headers: { 'x-auth-token': token } });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.msg || 'Reviews could not be loaded.');
      setReviews(Array.isArray(data.reviews) ? data.reviews : []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) loadReviews(); }, [token, loadReviews]);

  const updateStatus = async (reviewId, status) => {
    setSavingId(reviewId);
    setError('');
    try {
      const response = await fetch(`/api/admin/reviews/${reviewId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
        body: JSON.stringify({ status }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.msg || 'Review status could not be saved.');
      setReviews((current) => current.map((review) => review.id === reviewId ? { ...review, status: data.review.status } : review));
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="admin-reviews-page container-fluid py-4">
      <div className="admin-cms-page-header d-flex justify-content-between align-items-center mb-4">
        <div>
          <p className="admin-about-page__eyebrow">Customer content</p>
          <h2 className="brand-font m-0">Review moderation</h2>
          <p className="text-muted mb-0">Only approved reviews from delivered orders appear on the public Reviews page.</p>
        </div>
        <button type="button" className="btn btn-outline-custom" onClick={loadReviews}>Refresh</button>
      </div>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {loading ? <div role="status" className="text-muted">Loading review queue…</div> : reviews.length === 0 ? (
        <div className="border rounded bg-card p-4 text-muted">No delivered-order reviews to moderate yet.</div>
      ) : (
        <div className="admin-review-list d-grid gap-3">
          {reviews.map((review) => (
            <article className="border rounded bg-card p-4" key={review.id}>
              <div className="d-flex flex-wrap justify-content-between gap-3">
                <div>
                  <h3 className="h5 mb-1">{review.reviewerName || 'Hunar customer'}</h3>
                  <div className="text-warning" aria-label={`${review.rating} out of 5 stars`}>{'★'.repeat(review.rating)}<span className="text-muted">{'★'.repeat(5 - review.rating)}</span></div>
                  <p className="text-muted small mb-2">{review.productNames || 'Delivered Hunar order'} · {review.createdAt ? new Date(review.createdAt).toLocaleDateString() : ''}</p>
                </div>
                <label className="small fw-bold">
                  Moderation status
                  <select className="form-select mt-1" value={review.status || 'pending'} disabled={savingId === review.id} onChange={(event) => updateStatus(review.id, event.target.value)}>
                    {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                  </select>
                </label>
              </div>
              {review.comment && <p className="mb-3">{review.comment}</p>}
              {review.images?.length > 0 && (
                <div className="reviews-card__images" aria-label="Customer review images">
                  {review.images.map((image) => <a key={image.id} href={image.url} target="_blank" rel="noopener noreferrer"><img src={image.url} alt={image.filename || 'Review'} loading="lazy" /></a>)}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
